import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { PAGE_SIZE_MAX, PROJECT_STATUS, tutorialConstants } from '../../../utils/constants';
import { getTutorials, setActiveTutorial } from '../../../store/tutorials';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';
import { setEndDate, setFilters, setInspectionType, setLimit, setProjectStatus, setStartDate } from '../../../store/projectsQuery';

import _ from 'lodash';

import Button from '../../themed/button';
import DatePickerInput from '../../themed/date-picker';
import Elevation from '../../themed/instruction-modals/elevation';
import Conditional from '../../common/conditional';
import Grid from '../../blocks/grid';
import Filters from '../../blocks/filters';
import Header from '../../blocks/header';
import Modal from '../../themed/modal';
import NoItems from '../../blocks/no-items';
import Instruction from '../../themed/instruction-modals/instruction';
import ItemCount from '../../blocks/item-count';
import ProjectStatusButton from '../../themed/project-status-button';
import ReportOverview from '../../blocks/report-overview';
import Select from '../../themed/select/select';
import SyncTime from '../../blocks/sync-time';
import Table from '../../common/table';

import withCustomerProjects, { ExternalProjectsProps } from '../../hoc/withCustomerProjects';
import withInspectionTypes from '../../hoc/withInspectionTypes';
import useExternalDashboard from '../../../hooks/useExternalDashboard';

import css from './projects.module.scss';
import routes from '../../../utils/routes';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface OrganizationDashboardProps extends ExternalProjectsProps {
	types: models.SelectOption[];
}

const OrganizationDashboard: FC<OrganizationDashboardProps> = (props) => {
	const [sortColumn, setSortColumn] = useState(initialSortColumn);
	const [isOverviewVisible, setOverviewVisible] = useState(false);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const tutorials = useAppSelector(getTutorials);
	
	const projectsQuery = useAppSelector((state) => state.projectsQuery);
	const externalDashboard = useExternalDashboard();
	const isActive = tutorials.activeTutorial === tutorialConstants.INSPECTION_REPORT;

	const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const pageSize: number = +e.target.value;
		dispatch(setLimit(pageSize));
	};

	const handleViewNextTutorial = () => {
		dispatch(setActiveTutorial(tutorialConstants.PROFILE_SETTINGS));
		navigate(routes.profileSettings.fullPath);
	};

	const handleClearDateSelections = () => {
		dispatch(setStartDate(undefined));
		dispatch(setEndDate(undefined));
	};

	const handleFilterChange = (newFilters: models.FilterItem[]) => {
		dispatch(setFilters(newFilters));
	};

	const handleInspectionTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		dispatch(setInspectionType(e.target.value));
	};

	const toggleOverviewVisible = (): void => {
		setOverviewVisible((previous: boolean) => !previous);
	};

	const renderStatusButtons = () => {
		const PERMITTED_STATUSES = _.pick(PROJECT_STATUS, ['UNDER_CORRECTION', 'PUBLISHED_FOR_EXTERNAL_REVIEW', 'PUBLISHED_FOR_EXTERNAL_APPROVAL', 'COMPLETED']);
		const projectStatuses = [["All Projects", undefined]].concat(Object.entries(PERMITTED_STATUSES));

		return (
			<>
				{projectStatuses.map(([label, status]) => (
					<ProjectStatusButton
						key={label}
						status={status ?? label!}
						isActive={projectsQuery.projectStatus ? projectsQuery.projectStatus.includes(status!) : false}
						onClick={() => dispatch(setProjectStatus(status!))}
					/>
				))}
			</>
		);
	};

	const columns: models.Column[] = useMemo(() => {
		return [
			{
				path: 'documentNumber',
				label: 'Document No.',
				content: (project: models.Project) => (
					<Elevation isElevated={isActive}>
						<Link
							to={`/dashboard/projects/${project._id}`}
							className={isActive ? css.documentNumberActive : css.documentNumber}
						>
							{project.documentNumber}
						</Link>
					</Elevation>
				),
			},
			{
				path: 'createdAt',
				label: 'Date Created',
				content: (item: models.Project) => <span>{utils.formatTime(item.createdAt)}</span>,
			},
			{
				path: 'lastUpdatedAt',
				label: 'Last Updated',
				content: (item: models.Project) => <span>{utils.formatTime(item.lastUpdatedAt)}</span>,
			},
			{ path: 'facilityName', label: 'Facility' },
			{ path: 'module', label: 'Module' },
			{ path: 'inspectionTypeName', label: 'Inspection Type' },
			{
				path: 'projectStatus',
				label: 'Status',
				content: (item: models.Project) => <ProjectStatusButton isActive status={item.projectStatus} />
			},
		];
	}, [isActive, dispatch, navigate]);

	return (
		<Fragment>
			<Instruction
				isVisible={isActive}
				onProceed={handleViewNextTutorial}
				title="Your reports will appear here!"
				message="Reports that you have been tagged in will appear here! You can filter by several criteria to access different reports. Click on the DOCUMENT NUMBER to view more details"
			/>

			<Modal isVisible={isOverviewVisible} onClose={toggleOverviewVisible} style={{ maxWidth: '60%' }}>
				<ReportOverview isLoading={props.customerProjectsLoading} projects={props.projects} />
			</Modal>

			<Header subtitle="Manage Your Reports" />

			<div className={css['projects-body']}>
				<SyncTime lastSynced={props.customerProjectsLastUpdated} />

				<div className="divider my-4"></div>

				<div className={'container-fluid overflow-hidden ' + css.grid}>
					<div className="row gx-5">
						<Grid
							total={externalDashboard.overview.totalReports}
							label="Total Reports"
							icon="bag-plus"
							loading={externalDashboard.loading}
						/>

						<Grid
							total={externalDashboard.overview.totalActiveReports}
							label="In Progress"
							icon="activity"
							loading={externalDashboard.loading}
						/>

						<Grid
							total={externalDashboard.overview.acceptedReports}
							label="Accepted Reports"
							icon="check2-circle"
							loading={externalDashboard.loading}
						/>
					</div>
				</div>

				<div className="divider my-4"></div>
				
				<div className="d-flex justify-content-between align-items-center">
					<div className={utils.concatClassNames([css['projects-date-range'], 'justify-content-start'])}>
						{renderStatusButtons()}
					</div>
					
					<div className="d-flex justify-content-center align-items-center">
						<div className={css['projects-date-range']}>
							<DatePickerInput
								date={projectsQuery.startDate ? utils.getDateFromString(projectsQuery.startDate) : new Date()}
								onDateChange={(date: Date) => dispatch(setStartDate(date))}
								placeholderText="Select Start Date"
								maxDate={new Date()}
							/>

							<i className="bi bi-calendar-minus mx-3"></i>

							<DatePickerInput
								date={projectsQuery.endDate ? utils.getDateFromString(projectsQuery.endDate) : new Date()}
								onDateChange={(date: Date) => dispatch(setEndDate(date))}
								placeholderText="Select End Date"
								maxDate={new Date()}
							/>
						</div>

						<Conditional isVisible={!!projectsQuery.startDate || !!projectsQuery.endDate}>
							<div className="mx-4">
								<Button
									label="Clear Date Selections"
									icon="x-circle-fill"
									style={{ marginTop: 0 }}
									onClick={handleClearDateSelections}
								/>
							</div>
						</Conditional>
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="d-flex justify-content-end align-items-center">
					<div className="d-flex p-2 bg-white">
						<div className="me-3">
							<Select
								name="page-size"
								placeholder="All Inspection Type(s)"
								options={props.types}
								value={projectsQuery.inspectionTypeId}
								onChange={handleInspectionTypeChange}
							/>
						</div>

						<Filters 
							filters={projectsQuery.searchFields || []} 
							filterOptions={searchOptions} 
							onFilterChange={handleFilterChange} 
						/>

						<div className="mx-3">
							<Button
								label="Download"
								icon="download"
								style={{ backgroundColor: '#ab3c3c', margin: 0 }}
								onClick={toggleOverviewVisible}
							/>
						</div>

						<div className="ms-3">
							<Button
								onClick={props.refreshCustomerProjects}
								label="Refresh"
								icon="arrow-clockwise"
								style={{ margin: 0 }}
							/>
						</div>
					</div>
				</div>

				<div className="divider my-4"></div>


				<div className={css['projects-body-table']}>
					<Table
						columns={columns}
						data={props.projects}
						sortColumn={sortColumn}
						onSort={setSortColumn}
						isFetching={props.customerProjectsLoading}
					/>

					<NoItems
						isVisible={props.projects.length === 0}
						onRefresh={props.refreshCustomerProjects}
					/>

					<div className="d-flex justify-content-between align-items-center mt-3">
						<Select
							name="page-size"
							placeholder={'Showing (' + projectsQuery.limit + 'rows)'}
							options={utils.transformPageRange(PAGE_SIZE_MAX)}
							value={projectsQuery.limit}
							onChange={handlePageSizeChange}
						/>

						<div className="d-flex justify-content-center gap-3 align-items-center">
							<ItemCount count={props.dataLength} label='Loaded Reports' />

							{props.hasNextPage && (
								<Button 
									style={{ marginTop: 0 }}
									icon='arrow-clockwise' 
									label='Load More Reports...'
									onClick={props.fetchNextPage}
								/>
							)}
						</div>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

const searchOptions: models.SelectOption[] = [
	{ id: 'projectTitle', name: 'Project Title' },
	{ id: 'facilityName', name: 'Facility' },
	{ id: 'equipmentTag', name: 'Equipment Tag No' },
	{ id: 'equipmentDescription', name: 'Equipment Description' },
	{ id: 'documentNumber', name: 'Document No' },
	{ id: 'serialNumber', name: 'Report Number' },
	{ id: 'location', name: 'Location' },
	{ id: 'module', name: 'Module' },
	{ id: 'year', name: 'Year' },
	{ id: 'inspectedItemIDs', name: 'Inspected Items' },
];

const initialSortColumn: models.SortColumn = {
	path: 'lastUpdatedAt',
	order: 'desc',
};

export default withCustomerProjects(withInspectionTypes(OrganizationDashboard));
